<template>
  <ModalDialog
    ref="dialog"
    title="Complete Pending Panel Tasks"
    icon="fa-clipboard-list-check"
    :showFooter="true"
    :scrollable="true"
    :submitDisabled="taskCompleteCount == 0 || tasksHaveErrors()"
    :submitting="submitting"
    closeTitle="Cancel"
    :submitTitle="submitTitle"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="form" :labelColumnSize="3">
      <div class="row">
        <div class="col-6">
          <h2>Task List</h2>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="p-0">
            <StationProgressTrackerItem
              v-for="stageTask of taskList"
              :key="stageTask.id"
              :title="stageTask.productionStage.name"
              :completed="stageTask.status == ProductionPanelStageTaskStatusTypes.completed"
              :current="isTaskCurrent(taskList, stageTask)"
              :highlighted="isTaskToBeCompleted(stageTask)"
              :dueDate="stageTask.dueDate"
              :completedDate="stageTask.completionDate">

              <div v-if="stageTask.editable" class="task-editable-area">
                <div v-for="orderItem of stageTask.orderItems" :key="orderItem.id" class="task-editable-item mb-2">
                  <div class="row">
                    <div class="col-9 font-weight-bold">
                      {{orderItem.description}}
                      <template v-if="orderItem.edgeworkDescription">
                        - {{orderItem.edgeworkDescription}}
                      </template>
                    </div>
                    <div class="col-3 text-right">
                      <FormSwitch v-model="orderItem.selected" booleanMode />
                    </div>
                  </div>
                </div>
                <div v-if="isTaskSemiCompleted(stageTask)">
                  <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="text-danger" iconStack="fa-circle" iconStackClass="text-white" class="mr-1"></Icon>
                  All items must be flagged as completed
                </div>
                <div v-if="isTaskCompletedAhead(stageTask)">
                  <Icon iconStyle="fas" icon="fa-exclamation-circle" iconClass="text-danger" iconStack="fa-circle" iconStackClass="text-white" class="mr-1"></Icon>
                  Tasks must be completed in previous stages
                </div>
              </div>
            </StationProgressTrackerItem>
          </div>
        </div>
      </div>

    </FormGroup>
  </ModalDialog>
</template>

<style scoped>
.task-editable-area {
  padding-top: 0.5rem;
  padding-left: 2rem;
  padding-right: 2rem;
  font-size: 0.9rem;
}
.task-editable-title {
  font-size: 0.95rem;
}
.task-editable-item {
  padding-left: 0.5rem;
  font-size: 0.9rem;
}
</style>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import StationProgressTrackerItem from '@/apps/glass-production-station/src/components/schedule/stationProgressTrackerItem'

import { ProductionPanelStageTaskStatusTypes } from '@/store/types'

export default {
  name: 'FastForwardCompleteDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    StationProgressTrackerItem
  },
  props: {
    productionStageId: Number,
    productionStationId: Number,
    fastForwardOptions: Object
  },
  data() {
    return {
      form: {},
      taskList: [],
      submitting: false
    }
  },
  created() {
    this.ProductionPanelStageTaskStatusTypes = ProductionPanelStageTaskStatusTypes
  },
  mounted() {
  },
  computed: {
    taskCompleteCount() {
      return this.taskList.reduce((curCount, task) => {
        return curCount + (this.isTaskToBeCompleted(task) ? 1 : 0)
      }, 0)
    },
    submitTitle() {
      let completeCount = this.taskCompleteCount

      if(completeCount == 0)
        return 'Select Tasks'
      else if(completeCount == 1)
        return 'Complete 1 Task'
      return `Complete ${completeCount} Tasks`
    }    
  },
  methods: {
    show() {
      this.form = {}
      this.submitting = false

      this.taskList = this.fastForwardOptions.panelStageTasks.map(task => {
        let taskInFastForwardStageList = this.fastForwardOptions.stageList.find(stage => stage.id == task.productionStageId)
        var completeFlag = false
        if(task.status == ProductionPanelStageTaskStatusTypes.completed)
          completeFlag = true

        var orderItems = []
        if(taskInFastForwardStageList) {
          orderItems = taskInFastForwardStageList.orderItems.map(orderItem => {
            var item = _.cloneDeep(orderItem)
            item.selected = false
            return item
          })
        }

        return {
          id: task.id,
          status: task.status,
          dueDate: task.dueDate,
          completionDate: task.completionDate,
          productionStage: task.productionStage,
          taskOrder: task.taskOrder,
          editable: taskInFastForwardStageList != null,
          completeFlag: completeFlag,
          orderItems: orderItems
        }
      })

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.submitting = true
      this.form = this.clearFormResponse()

      // Generate task completion
      var stageTaskIdList = this.taskList
      .filter(task => this.isTaskToBeCompleted(task))
      .map(task => task.id)

      var pendingStageTaskList = this.taskList.filter(task => task.editable)
      var completingAllTasks = pendingStageTaskList.length == stageTaskIdList.length

      let completionData = {
        productionPanelId: this.fastForwardOptions.productionPanelId,
        stageTaskIdList: stageTaskIdList
      }

      this.$apiService.glassProductionStationService.completePanelTasks(this.productionStationId, completionData)
      .then(response => {
        this.modalClosed({ allTasksCompleted: completingAllTasks })
        this.hide()
      })
      .catch(err => {
        this.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.submitting = false
      })
    },

    isTaskCurrent(stageTaskList, stageTask) {
      if(stageTask.status == ProductionPanelStageTaskStatusTypes.production)
        return true

      // If pending, and all tasks have been completed up to here, then this task is current
      // If no prior tasks, then this is the first & current task
      if(stageTask.status == ProductionPanelStageTaskStatusTypes.pending) {
        let previousTask = stageTaskList.find(taskItem => taskItem.taskOrder == stageTask.taskOrder-1)
        if(!previousTask || previousTask.status == ProductionPanelStageTaskStatusTypes.completed)
          return true
      }
      return false
    },
    isTaskToBeCompleted(stageTask) {
      if(stageTask.status != ProductionPanelStageTaskStatusTypes.pending && stageTask.status != ProductionPanelStageTaskStatusTypes.production)
        return false
      if(stageTask.orderItems.length == 0)
        return false

      return stageTask.orderItems.filter(orderItem => orderItem.selected).length == stageTask.orderItems.length
    },
    isTaskSemiCompleted(stageTask) {
      if(stageTask.status != ProductionPanelStageTaskStatusTypes.pending && stageTask.status != ProductionPanelStageTaskStatusTypes.production)
        return false
      if(stageTask.orderItems.length == 0)
        return false

      let completedCount = stageTask.orderItems.filter(orderItem => orderItem.selected).length

      return completedCount > 0 && completedCount < stageTask.orderItems.length
    },
    isTaskCompletedAhead(stageTask) {
      var taskCompletedAhead = false

      if(!this.isTaskToBeCompleted(stageTask))
        return false

      this.taskList.forEach(stage => {
        if(stage.taskOrder < stageTask.taskOrder) {
          if(stage.status == ProductionPanelStageTaskStatusTypes.completed)
            return

          if(!this.isTaskToBeCompleted(stage)) {
            // If a floating stage, it can be skipped
            if(!this.isTaskFloatingStage(stage))
              taskCompletedAhead = true
          }
        }
      })
      
      return taskCompletedAhead
    },
    isTaskFloatingStage(stageTask) {
      return stageTask.productionStage.isFloatingStage
    },
    tasksHaveErrors() {
      var errorCount = 0
      this.taskList.forEach(stage => {
        if(this.isTaskSemiCompleted(stage))
          errorCount++
        else if(this.isTaskCompletedAhead(stage))
          errorCount++
      })
      return errorCount > 0
    }
  }
}
</script>